import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { useLogin } from "../data/useLogin";
import { extractErrors } from "../utils/api";
import { logError } from "../utils/error-handling";
import { env } from "../utils/env";
import { DateTime } from "luxon";
import { useUserDetails } from "../data/useUserDetails";
import { User } from "../types/User";
import { queryClient } from "../queryClient";
import { ROUTE } from "../routes";
import axios from "axios";
import { clearAllDashboardViews } from "../utils/viewStorage";

export enum UserVerificationStatus {
  /** User has not completed sign-up, needs to verify email. */
  EmailVerificationRequired,
  /** User has not completed sign-up, needs to create company. */
  CompanyRegistrationRequired,
  /** User has not completed sign-up, needs to create billing. */
  BillingInfoRequired,
  /** User has not completed sign-up, needs to create primary purpose. */
  PrimaryPurposeRequired,
  /** User has not completed sign-up, needs to create dpo. */
  DPORequired,
  /** User has completed sign-up, Tiller doing manual checks. */
  AccountPending,
  /** User has completed sign-up, allow to navigate to all routes. */
  Verified,
}

const determineUserVerificationStatus = (user: User) => {
  let userVerificationStatus: UserVerificationStatus;
  if (user) {
    if (!user.is_confirmed) {
      userVerificationStatus = UserVerificationStatus.EmailVerificationRequired;
    } else if (!user.tenant) {
      userVerificationStatus =
        UserVerificationStatus.CompanyRegistrationRequired;
    } else if (user.is_pending) {
      if (!user.is_billinginfo_added) {
        userVerificationStatus = UserVerificationStatus.BillingInfoRequired;
      } else if (user.is_billinginfo_added && !user.is_primary_purpose_added) {
        userVerificationStatus = UserVerificationStatus.PrimaryPurposeRequired;
      } else if (
        user.is_billinginfo_added &&
        user.is_primary_purpose_added &&
        !user.is_dpo_added
      ) {
        userVerificationStatus = UserVerificationStatus.DPORequired;
      } else {
        userVerificationStatus = UserVerificationStatus.AccountPending;
      }
    } else {
      userVerificationStatus = UserVerificationStatus.Verified;
    }
  }
  return userVerificationStatus;
};

interface Credentials {
  email: string;
  accessToken: string;
  refreshToken: string;
  expires_in: number;
  expiresAt: number;
}

interface Auth {
  credentials?: Credentials;
  setCredentials: (creds: Credentials | undefined) => void;
  isLoggedIn: boolean;
  login: {
    call: (email: string, password: string, redirectTo?: string) => void;
    isLoading: boolean;
    errors: any;
  };
  logout: (redirectTo?: string) => void;
  authHeader: string;
  userDetails: User | undefined;
  userVerificationStatus: UserVerificationStatus;
  isLoadingUserDetails: boolean;
  isAdmin: boolean;
  effectivePermissions: string[];
  hasPermission: (codename: string) => boolean;
}

export const AUTH_COOKIE_NAME = env.cookieName;
export const AuthContext = createContext<Auth | null>(null);

export const AuthProvider = (props: any) => {
  const { children } = props;
  const [cookies, setCookie, removeCookie] = useCookies();

  const [credentials, setCredentials] = useState<Credentials | undefined>(
    cookies[AUTH_COOKIE_NAME]
  );

  const isLoggedIn =
    Boolean(credentials?.accessToken) && Boolean(credentials?.email);

  const authHeader = `Bearer ${credentials?.accessToken}`;

  const login = useLogin();
  const navigate = useNavigate();

  const { data: user, isLoading: isLoadingUserDetails } = useUserDetails(
    isLoggedIn,
    authHeader
  );

  let userVerificationStatus = determineUserVerificationStatus(user?.data);

  const effectivePermissions = user?.data?.effective_permissions ?? [];
  const hasPermission = (codename: string) =>
    effectivePermissions.includes(codename);

  const callLogin: Auth["login"]["call"] = (email, password, redirectTo) => {
    // on success add redirect path to go to authentication page
    return new Promise((resolve, reject) => {
      login.mutate(
        { email, password },
        {
          onSuccess: (res) => {
            resolve(res);
            if (res?.status?.toString().startsWith("2")) {
              const creds = {
                email,
                accessToken: res.data.access,
                refreshToken: res.data.refresh,
                expires_in: res.data.expires_in,
                expiresAt: Date.now() + res.data.expires_in * 1000,
              };
              setCredentials(creds);
              setCookie(AUTH_COOKIE_NAME, creds, {
                expires: DateTime.now()
                  .plus({ milliseconds: env.cookieExpiryMs })
                  .toJSDate(),
                path: "/",
                secure: true,
                sameSite: "strict",
              });

              navigate(redirectTo || ROUTE.ROOT);
            }
          },
          onError: (err: any) => logError(err),
        }
      );
    });
  };

  const logout: Auth["logout"] = (redirectTo?) => {
    const go = (to?: string) => navigate(to ?? ROUTE.ROOT);

    if (authHeader) {
      axios
        .post(
          `${env.verifyServiceUrl}user/logout/`,
          {},
          {
            headers: {
              Authorization: authHeader,
            },
          }
        )
        .then(() => {
          setCredentials(undefined);
          queryClient.removeQueries();
          removeCookie(AUTH_COOKIE_NAME);
          clearAllDashboardViews();
          go(redirectTo);
        })
        .catch((err) => {
          logError(err);
        })
        .finally(() => {
          setCredentials(undefined);
          queryClient.removeQueries();
          removeCookie(AUTH_COOKIE_NAME);
          clearAllDashboardViews();
          go(redirectTo);
        });
    } else {
      setCredentials(undefined);
      queryClient.removeQueries();
      removeCookie(AUTH_COOKIE_NAME);
      clearAllDashboardViews();
      go(redirectTo);
    }
  };

  const value = {
    credentials,
    setCredentials,
    isLoggedIn,
    login: {
      call: callLogin,
      isLoading: login.isLoading,
      errors: extractErrors(login.error),
    },
    logout,
    authHeader,
    userDetails: user?.data,
    isAdmin: Boolean(user?.data?.groups?.find(({ name }) => name === "Admin")),
    isLoadingUserDetails,
    userVerificationStatus,
    effectivePermissions,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext) as Auth;
};
